/**
 * How the /tasks list orders and trims its groups (KV, 2026-11-04: "sort by
 * due date", and only a handful of rows per group with a "Show all").
 *
 * Pure functions so the rules are tested without React. Sorting happens on
 * the client because the list already holds every task the filters return;
 * "Manual" keeps the server's order (drag-and-drop positions).
 */
import { dueDay, todayIn, viewerTimezone } from "./task-due"

export type TaskSortMode = "manual" | "due" | "priority" | "updated" | "created"

export const TASK_SORT_OPTIONS: { value: TaskSortMode; label: string }[] = [
  { value: "manual", label: "Manual" },
  { value: "due", label: "Due date" },
  { value: "priority", label: "Priority" },
  { value: "updated", label: "Recently updated" },
  { value: "created", label: "Newest" },
]

export function isTaskSortMode(value: unknown): value is TaskSortMode {
  return TASK_SORT_OPTIONS.some((o) => o.value === value)
}

/** Rows shown per group before "Show all". */
export type TaskSectionLimit = 10 | 25 | "all"

export const TASK_SECTION_LIMITS: { value: TaskSectionLimit; label: string }[] = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: "all", label: "All" },
]

export function parseSectionLimit(value: string | null | undefined): TaskSectionLimit {
  if (value === "all") return "all"
  if (value === "25") return 25
  return 10
}

type SortableTask = {
  id: number
  due_date: string | null
  due_at?: string | null
  priority: string
  created_at: string
  updated_at: string
}

const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 }

function priorityRank(task: SortableTask): number {
  return PRIORITY_RANK[task.priority] ?? 2
}

/**
 * Due order: by the day it is due in the viewer's zone, then, within a day,
 * timed tasks in time order before untimed ones (a dated task is due by that
 * day's close). Tasks with no due date sort after every dated one.
 */
function compareDue(a: SortableTask, b: SortableTask, tz: string): number {
  const ad = dueDay(a, tz)
  const bd = dueDay(b, tz)
  if (ad !== bd) {
    if (!ad) return 1
    if (!bd) return -1
    return ad < bd ? -1 : 1
  }
  const at = a.due_at ? Date.parse(a.due_at) : NaN
  const bt = b.due_at ? Date.parse(b.due_at) : NaN
  const av = Number.isNaN(at) ? Number.POSITIVE_INFINITY : at
  const bv = Number.isNaN(bt) ? Number.POSITIVE_INFINITY : bt
  return av === bv ? 0 : av < bv ? -1 : 1
}

/**
 * Orders a group's tasks. Ties fall back to the incoming (manual) order, so a
 * sort never shuffles equal rows between refetches.
 */
export function sortTasks<T extends SortableTask>(tasks: T[], mode: TaskSortMode, tz: string = viewerTimezone()): T[] {
  if (mode === "manual") return tasks
  const indexed = tasks.map((task, index) => ({ task, index }))
  const cmp = (a: { task: T; index: number }, b: { task: T; index: number }): number => {
    let r = 0
    switch (mode) {
      case "due":
        r = compareDue(a.task, b.task, tz) || priorityRank(a.task) - priorityRank(b.task)
        break
      case "priority":
        r = priorityRank(a.task) - priorityRank(b.task) || compareDue(a.task, b.task, tz)
        break
      case "updated":
        r = b.task.updated_at.localeCompare(a.task.updated_at)
        break
      case "created":
        r = b.task.created_at.localeCompare(a.task.created_at)
        break
    }
    return r || a.index - b.index
  }
  return indexed.sort(cmp).map((x) => x.task)
}

/** The rows a group shows, and how many wait behind "Show all". */
export function visibleRows<T>(tasks: T[], limit: TaskSectionLimit, expanded: boolean): { shown: T[]; hidden: number } {
  if (limit === "all" || expanded || tasks.length <= limit) return { shown: tasks, hidden: 0 }
  return { shown: tasks.slice(0, limit), hidden: tasks.length - limit }
}

// ── Group by week ───────────────────────────────────────────────────────────

export type WeekGroupKey = "overdue" | `week:${string}` | "later" | "none" | "closed"

export type WeekGroup<T> = { key: WeekGroupKey; name: string; tasks: T[] }

/** Weeks listed one by one before the rest fall into "Later": this week and three more. */
export const WEEKS_SHOWN = 4

function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

/** The first day of `day`'s week; weekStartsOn 0 = Sunday, 1 = Monday. */
export function weekStartOf(day: string, weekStartsOn: 0 | 1 = 1): string {
  const [y, m, d] = day.split("-").map(Number)
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  const back = (dow - weekStartsOn + 7) % 7
  return addDays(day, -back)
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** "Nov 2 – 8", or "Oct 26 – Nov 1" across a month. */
export function weekRangeLabel(start: string): string {
  const end = addDays(start, 6)
  const [, sm, sd] = start.split("-").map(Number)
  const [, em, ed] = end.split("-").map(Number)
  return sm === em ? `${MONTHS[sm - 1]} ${sd} – ${ed}` : `${MONTHS[sm - 1]} ${sd} – ${MONTHS[em - 1]} ${ed}`
}

/**
 * Buckets tasks by the week they are due, in the viewer's zone: Overdue
 * (before today), this week, the next WEEKS_SHOWN - 1 weeks one by one,
 * Later, then No due date. Each bucket is ordered by due date. Only buckets
 * with tasks are returned, except this week, which is always there so the
 * list has an anchor. Done and canceled tasks (when the status filter shows
 * them) are never overdue; they go to a last "Done" bucket, so the weeks
 * hold only work still to do.
 */
export function groupTasksByWeek<T extends SortableTask>(
  tasks: T[],
  opts: { today?: string; tz?: string; weekStartsOn?: 0 | 1; isClosed?: (task: T) => boolean } = {},
): WeekGroup<T>[] {
  const tz = opts.tz ?? viewerTimezone()
  const today = opts.today ?? todayIn(tz)
  const thisWeek = weekStartOf(today, opts.weekStartsOn ?? 1)
  const laterFrom = addDays(thisWeek, 7 * WEEKS_SHOWN)

  const overdue: T[] = []
  const later: T[] = []
  const none: T[] = []
  const closed: T[] = []
  const weeks = new Map<string, T[]>()
  for (let i = 0; i < WEEKS_SHOWN; i++) weeks.set(addDays(thisWeek, 7 * i), [])

  for (const task of tasks) {
    const day = dueDay(task, tz)
    if (opts.isClosed?.(task)) closed.push(task)
    else if (!day) none.push(task)
    else if (day < today) overdue.push(task)
    else if (day >= laterFrom) later.push(task)
    else weeks.get(weekStartOf(day, opts.weekStartsOn ?? 1))!.push(task)
  }

  const byDue = (list: T[]) => sortTasks(list, "due", tz)
  const groups: WeekGroup<T>[] = []
  if (overdue.length) groups.push({ key: "overdue", name: "Overdue", tasks: byDue(overdue) })
  let i = 0
  for (const [start, list] of weeks) {
    const label = i === 0 ? "This week" : i === 1 ? "Next week" : null
    const range = weekRangeLabel(start)
    if (i === 0 || list.length) {
      groups.push({ key: `week:${start}`, name: label ? `${label} (${range})` : range, tasks: byDue(list) })
    }
    i++
  }
  if (later.length) groups.push({ key: "later", name: "Later", tasks: byDue(later) })
  if (none.length) groups.push({ key: "none", name: "No due date", tasks: none })
  if (closed.length) groups.push({ key: "closed", name: "Done", tasks: byDue(closed) })
  return groups
}
